/**
 * Canonical Bible book list + abbreviation/alias map.
 *
 * The 66 canonical book *names* are derived from the live Sola API
 * (`GET /api/chapter/structure`) — see scripts/fetch-bible-structure.ts,
 * whose output is committed at src/lib/generated/bible-structure.json.
 * This file cross-checks against that generated list (see the invariant
 * check at the bottom) so the two can never silently drift apart.
 *
 * The alias/abbreviation table itself is hand-authored (the API only gives
 * us names + chapter/verse counts, not alternate spellings) — this is the
 * "small client-side normalizer" called for in VERSE_EXPLAINED_PLAN.md /
 * docs-research/api-surface-report.md §4, which notes the backend's own
 * `getStandardBookName()` only handles no-space abbreviations like "1CO",
 * not spaced/informal forms like "1 Cor" or "Jn".
 */
import bibleStructure from './generated/bible-structure.json';

export type Testament = 'OT' | 'NT';

export interface BookMeta {
  /** Canonical name exactly as returned by the Sola API (`/api/chapter/structure`). */
  name: string;
  /** URL-slug token for this book, e.g. "psalm" (not "psalms"), "song-of-solomon". */
  slug: string;
  testament: Testament;
  /** Lowercase, whitespace-normalized aliases (including the canonical name itself). */
  aliases: string[];
}

// Canonical order (Protestant 66-book canon) — the API's own key order is
// not canonical (it reflects insertion order in the source DB), so the
// book-picker UI needs an explicit ordered list.
export const BOOKS: BookMeta[] = [
  // Old Testament
  book('Genesis', 'genesis', 'OT', ['gen', 'ge', 'gn']),
  book('Exodus', 'exodus', 'OT', ['exod', 'ex']),
  book('Leviticus', 'leviticus', 'OT', ['lev', 'le', 'lv']),
  book('Numbers', 'numbers', 'OT', ['num', 'nu', 'nm', 'nb']),
  book('Deuteronomy', 'deuteronomy', 'OT', ['deut', 'dt']),
  book('Joshua', 'joshua', 'OT', ['josh', 'jos', 'jsh']),
  book('Judges', 'judges', 'OT', ['judg', 'jdg', 'jg', 'jdgs']),
  book('Ruth', 'ruth', 'OT', ['rth', 'ru']),
  book('1 Samuel', '1-samuel', 'OT', ['1 sam', '1sam', '1 sm', 'i samuel', 'first samuel', '1st samuel']),
  book('2 Samuel', '2-samuel', 'OT', ['2 sam', '2sam', '2 sm', 'ii samuel', 'second samuel', '2nd samuel']),
  book('1 Kings', '1-kings', 'OT', ['1 kgs', '1kgs', 'i kings', 'first kings', '1st kings']),
  book('2 Kings', '2-kings', 'OT', ['2 kgs', '2kgs', 'ii kings', 'second kings', '2nd kings']),
  book('1 Chronicles', '1-chronicles', 'OT', ['1 chron', '1chr', '1 chr', 'i chronicles', 'first chronicles', '1st chronicles']),
  book('2 Chronicles', '2-chronicles', 'OT', ['2 chron', '2chr', '2 chr', 'ii chronicles', 'second chronicles', '2nd chronicles']),
  book('Ezra', 'ezra', 'OT', ['ezr']),
  book('Nehemiah', 'nehemiah', 'OT', ['neh', 'ne']),
  book('Esther', 'esther', 'OT', ['esth', 'est']),
  book('Job', 'job', 'OT', ['jb']),
  book('Psalms', 'psalm', 'OT', ['psalm', 'psa', 'ps', 'pslm', 'psm', 'pss']),
  book('Proverbs', 'proverbs', 'OT', ['prov', 'pro', 'prv', 'pr']),
  book('Ecclesiastes', 'ecclesiastes', 'OT', ['eccles', 'eccl', 'ecc', 'qoh']),
  book('Song of Solomon', 'song-of-solomon', 'OT', ['song', 'sos', 'song of songs', 'canticles', 'cant']),
  book('Isaiah', 'isaiah', 'OT', ['isa', 'is']),
  book('Jeremiah', 'jeremiah', 'OT', ['jer', 'je']),
  book('Lamentations', 'lamentations', 'OT', ['lam']),
  book('Ezekiel', 'ezekiel', 'OT', ['ezek', 'eze', 'ezk']),
  book('Daniel', 'daniel', 'OT', ['dan', 'da', 'dn']),
  book('Hosea', 'hosea', 'OT', ['hos', 'ho']),
  book('Joel', 'joel', 'OT', ['jl']),
  book('Amos', 'amos', 'OT', ['am']),
  book('Obadiah', 'obadiah', 'OT', ['obad', 'ob']),
  book('Jonah', 'jonah', 'OT', ['jon']),
  book('Micah', 'micah', 'OT', ['mic', 'mc']),
  book('Nahum', 'nahum', 'OT', ['nah', 'na']),
  book('Habakkuk', 'habakkuk', 'OT', ['hab', 'hb']),
  book('Zephaniah', 'zephaniah', 'OT', ['zeph', 'zep', 'zp']),
  book('Haggai', 'haggai', 'OT', ['hag', 'hg']),
  book('Zechariah', 'zechariah', 'OT', ['zech', 'zec', 'zc']),
  book('Malachi', 'malachi', 'OT', ['mal', 'ml']),
  // New Testament
  book('Matthew', 'matthew', 'NT', ['matt', 'mt']),
  book('Mark', 'mark', 'NT', ['mrk', 'mk', 'mr']),
  book('Luke', 'luke', 'NT', ['luk', 'lk']),
  book('John', 'john', 'NT', ['jhn', 'jn']),
  book('Acts', 'acts', 'NT', ['act', 'ac']),
  book('Romans', 'romans', 'NT', ['rom', 'ro', 'rm']),
  book('1 Corinthians', '1-corinthians', 'NT', ['1 cor', '1cor', '1 co', 'i corinthians', 'first corinthians', '1st corinthians']),
  book('2 Corinthians', '2-corinthians', 'NT', ['2 cor', '2cor', '2 co', 'ii corinthians', 'second corinthians', '2nd corinthians']),
  book('Galatians', 'galatians', 'NT', ['gal', 'ga']),
  book('Ephesians', 'ephesians', 'NT', ['eph']),
  book('Philippians', 'philippians', 'NT', ['phil', 'php', 'pp']),
  book('Colossians', 'colossians', 'NT', ['col']),
  book('1 Thessalonians', '1-thessalonians', 'NT', ['1 thess', '1thess', '1 th', 'i thessalonians', 'first thessalonians', '1st thessalonians']),
  book('2 Thessalonians', '2-thessalonians', 'NT', ['2 thess', '2thess', '2 th', 'ii thessalonians', 'second thessalonians', '2nd thessalonians']),
  book('1 Timothy', '1-timothy', 'NT', ['1 tim', '1tim', '1 ti', 'i timothy', 'first timothy', '1st timothy']),
  book('2 Timothy', '2-timothy', 'NT', ['2 tim', '2tim', '2 ti', 'ii timothy', 'second timothy', '2nd timothy']),
  book('Titus', 'titus', 'NT', ['tit', 'ti']),
  book('Philemon', 'philemon', 'NT', ['philem', 'phm', 'pm']),
  book('Hebrews', 'hebrews', 'NT', ['heb']),
  book('James', 'james', 'NT', ['jas', 'jm']),
  book('1 Peter', '1-peter', 'NT', ['1 pet', '1pet', '1 pe', 'i peter', 'first peter', '1st peter']),
  book('2 Peter', '2-peter', 'NT', ['2 pet', '2pet', '2 pe', 'ii peter', 'second peter', '2nd peter']),
  book('1 John', '1-john', 'NT', ['1 jn', '1jn', '1 jo', 'i john', 'first john', '1st john']),
  book('2 John', '2-john', 'NT', ['2 jn', '2jn', '2 jo', 'ii john', 'second john', '2nd john']),
  book('3 John', '3-john', 'NT', ['3 jn', '3jn', '3 jo', 'iii john', 'third john', '3rd john']),
  book('Jude', 'jude', 'NT', ['jud', 'jd']),
  book('Revelation', 'revelation', 'NT', ['rev', 're', 'apocalypse']),
];

function book(name: string, slug: string, testament: Testament, extraAliases: string[]): BookMeta {
  const aliases = new Set<string>([name.toLowerCase(), ...extraAliases.map((a) => a.toLowerCase())]);
  return { name, slug, testament, aliases: Array.from(aliases) };
}

// --- Lookup indices --------------------------------------------------------

/** normalized alias (spaces collapsed) -> BookMeta */
const aliasIndex = new Map<string, BookMeta>();
/** alias with all whitespace stripped -> BookMeta (covers "1cor", "1co13" typed forms) */
const noSpaceAliasIndex = new Map<string, BookMeta>();
/** slug token -> BookMeta, longest slug first for prefix matching */
export const bookBySlug = new Map<string, BookMeta>();
/** canonical API name -> BookMeta */
export const bookByName = new Map<string, BookMeta>();

for (const b of BOOKS) {
  bookBySlug.set(b.slug, b);
  bookByName.set(b.name, b);
  for (const alias of b.aliases) {
    const normalized = normalizeWhitespace(alias);
    aliasIndex.set(normalized, b);
    noSpaceAliasIndex.set(normalized.replace(/\s+/g, ''), b);
  }
}

/** Slug tokens sorted longest-first, for prefix matching in slugToReference. */
export const bookSlugsByLengthDesc = [...bookBySlug.keys()].sort((a, b) => b.length - a.length);

function normalizeWhitespace(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

const ORDINAL_WORDS: Record<string, string> = {
  first: '1',
  second: '2',
  third: '3',
  i: '1',
  ii: '2',
  iii: '3',
};

/**
 * Resolve free-text book input ("jn", "1 Cor", "First Corinthians", "psalm")
 * to its canonical BookMeta, or null if unrecognized.
 */
export function resolveBookName(input: string): BookMeta | null {
  let normalized = normalizeWhitespace(input).replace(/\./g, '');
  if (!normalized) return null;

  // "First Corinthians" / "I Corinthians" -> "1 corinthians"
  const [first, ...rest] = normalized.split(' ');
  if (ORDINAL_WORDS[first] && rest.length) {
    normalized = `${ORDINAL_WORDS[first]} ${rest.join(' ')}`;
  }

  const direct = aliasIndex.get(normalized);
  if (direct) return direct;

  const noSpace = noSpaceAliasIndex.get(normalized.replace(/\s+/g, ''));
  if (noSpace) return noSpace;

  return null;
}

// --- Structure (chapter/verse counts), from the committed generated file ---

export interface BibleStructure {
  generatedAt: string;
  source: string;
  translation: string;
  structure: Record<string, Record<string, number>>;
}

export const structure: BibleStructure = bibleStructure as BibleStructure;

export function chapterCount(bookName: string): number {
  const chapters = structure.structure[bookName];
  return chapters ? Object.keys(chapters).length : 0;
}

export function verseCount(bookName: string, chapter: number): number {
  const chapters = structure.structure[bookName];
  if (!chapters) return 0;
  return chapters[String(chapter)] ?? 0;
}

export function isValidChapter(bookName: string, chapter: number): boolean {
  return chapter >= 1 && chapter <= chapterCount(bookName);
}

export function isValidVerse(bookName: string, chapter: number, verse: number): boolean {
  const count = verseCount(bookName, chapter);
  return count > 0 && verse >= 1 && verse <= count;
}

// --- Invariant check: every book in BOOKS must exist in the generated
// structure (and vice versa), so this hand-authored alias table can never
// silently drift from the live API's book list. Runs once at module load
// (cheap — 66-element set comparison); throws in dev/build if it ever
// drifts, so the discrepancy is caught immediately rather than silently
// producing broken slugs.
(function assertBookListMatchesGeneratedStructure() {
  const generatedNames = new Set(Object.keys(structure.structure));
  const handAuthoredNames = new Set(BOOKS.map((b) => b.name));
  const missingFromHandAuthored = [...generatedNames].filter((n) => !handAuthoredNames.has(n));
  const missingFromGenerated = [...handAuthoredNames].filter((n) => !generatedNames.has(n));
  if (missingFromHandAuthored.length || missingFromGenerated.length) {
    throw new Error(
      `bibleBooks.ts has drifted from src/lib/generated/bible-structure.json. ` +
        `Missing from hand-authored BOOKS: ${missingFromHandAuthored.join(', ') || 'none'}. ` +
        `Missing from generated structure: ${missingFromGenerated.join(', ') || 'none'}. ` +
        `Re-run scripts/fetch-bible-structure.ts and update src/lib/bibleBooks.ts.`
    );
  }
})();
